import React, { useMemo, useEffect, useState } from 'react';
import './RegulatoryBodyReport.scss';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useGridApiContext } from '@mui/x-data-grid';

import DeviceLink from 'src/component/UI/DeviceLink';
import { getClient, getTemperatureUnit } from 'src/module/authentication/selector';
import { generateTestingReportPdf } from 'src/module/device/action';
import { useTranslation } from 'src/i18n';
import { useReportData } from 'src/hooks/useReportData';
import { useReportColumns } from 'src/hooks/useReportColumns';
import { formatTemperature, formatLoadCurrent, getFieldLabel as getFieldLabelUtil } from 'src/utils/reportUtils';
import { isWhitelabel } from 'src/utils/utils';
import BaseReport from 'src/component/Report/BaseReport';
import ReportToolbar from 'src/component/Report/ReportToolbar';

/**
 * Creates a device link column for controller or circuit tags
 */
const createDeviceLinkColumn = (
  field: string,
  headerName: string,
  isController: boolean = false
) => ({
  field,
  headerName,
  minWidth: 100,
  renderCell: (params: any) => {
    if (params.value === null || params.value === undefined || params.value === '') {
      return '-';
    }
    if (isController) {
      return <DeviceLink label={params.value} controllerTag={params.value} />;
    }
    return (
      <DeviceLink
        label={params.value}
        circuitTag={params.value}
        controllerTag={params.row.controller_tag}
      />
    );
  },
  flex: 1
});

/**
 * Creates a formatted column for temperature or current values
 */
const createFormattedColumn = (
  field: string,
  headerName: string,
  formatter: (value: any, temperatureUnit?: string) => string,
  temperatureUnit?: string
) => ({
  field,
  headerName,
  minWidth: 100,
  valueFormatter: (params: any) => formatter(params.value, temperatureUnit),
  flex: 1
});

/**
 * Creates a default column with generic formatting
 */
const createDefaultColumn = (field: string, headerName: string, minWidth: number = 100) => ({
  field,
  headerName,
  minWidth,
  valueFormatter: (params: any) => {
    if (params.value === null || params.value === undefined || params.value === '') {
      return '-';
    }
    return params.value;
  },
  flex: 1
});

/**
 * Creates the combined controller/circuit column
 */
const createCombinedControllerCircuitColumn = (headerName: string) => ({
  field: 'controller_circuit',
  headerName,
  minWidth: 200,
  renderCell: (params: any) => {
    const controllerTag = params.row.controller_tag;
    const circuitTag = params.row.tag || params.row.circuit_tag;

    // If both tags exist, show combined format
    if (controllerTag && circuitTag) {
      return (
        <div style={{ padding: '4px 0' }}>
          <DeviceLink label={controllerTag} controllerTag={controllerTag} /> /{' '}
          <DeviceLink label={circuitTag} circuitTag={circuitTag} controllerTag={controllerTag} />
        </div>
      );
    }
    // If only controller tag exists
    if (controllerTag && !circuitTag) {
      return (
        <div style={{ padding: '4px 0' }}>
          <DeviceLink label={controllerTag} controllerTag={controllerTag} />
        </div>
      );
    }
    // If only circuit tag exists
    if (!controllerTag && circuitTag) {
      return (
        <div style={{ padding: '4px 0' }}>
          <DeviceLink label={circuitTag} circuitTag={circuitTag} controllerTag={null} />
        </div>
      );
    }
    // Neither exists
    return '-';
  },
  flex: 1
});

/**
 * Gets the list of fields to export, excluding the combined column
 */
const getExportFields = (data: any, columns: any[]): string[] => {
  const exportFields: string[] = [];
  const seenFields = new Set<string>();

  // Always exclude the combined column
  seenFields.add('controller_circuit');

  // Add controller_tag first if it exists in data
  if (data && data.size > 0) {
    const firstRow = data.toJS()[0];
    if (firstRow?.controller_tag !== undefined) {
      exportFields.push('controller_tag');
      seenFields.add('controller_tag');
    }

    // Add tag/circuit_tag second if it exists in data (prefer tag over circuit_tag)
    if (firstRow?.tag !== undefined) {
      exportFields.push('tag');
      seenFields.add('tag');
      seenFields.add('circuit_tag'); // Prevent circuit_tag from being added later
    } else if (firstRow?.circuit_tag !== undefined) {
      exportFields.push('circuit_tag');
      seenFields.add('circuit_tag');
      seenFields.add('tag'); // Prevent tag from being added later
    }
  }

  // Get all available field names from the first row of data (if available)
  const availableDataFields = data && data.size > 0
    ? new Set(Object.keys(data.toJS()[0] || {}))
    : new Set<string>();

  // Add all other fields from columns
  columns.forEach(col => {
    if (
      col.field &&
      !seenFields.has(col.field) &&
      availableDataFields.has(col.field)
    ) {
      exportFields.push(col.field);
      seenFields.add(col.field);
    }
  });

  return exportFields;
};

export default function MultiCircuitReport ({ filters }) {
  const { t } = useTranslation();
  const { data, loading, pdfLoading } = useReportData('multi');
  const client = useSelector(getClient);
  const temperatureUnit = useSelector(getTemperatureUnit);
  const dispatch = useDispatch();
  const [hasRun, setHasRun] = useState(false);
  const whitelabel = isWhitelabel();

  // Track if report has been run (not loading and data exists)
  useEffect(() => {
    if (!loading && data !== null && data !== undefined) {
      setHasRun(true);
    }
  }, [loading, data]);

  // Default field label mappings (can be overridden by client preferences)
  const defaultFieldLabels = useMemo(
    () => ({
      tag: t('multiCircuitReport.circuitTag'),
      controller_tag: t('multiCircuitReport.controllerTag'),
      controller_circuit: t('alarms.deviceTag'), // Use "Device Tag" for the combined column
      line_description: t('regulatoryReport.lineDescription'),
      status_temperatureSensors_1_temperature: t('multiCircuitReport.lastReadRtd1Temperature'),
      status_temperatureSensors_2_temperature: t('multiCircuitReport.lastReadRtd2Temperature'),
      status_loadCurrent: t('multiCircuitReport.lastReadLoadCurrent'),
      current_alarms: t('multiCircuitReport.currentAlarms')
    }),
    [t]
  );

  // Get custom field labels from client preferences, fall back to defaults
  const getFieldLabel = (fieldName: string): string => {
    return getFieldLabelUtil(fieldName, defaultFieldLabels, client);
  };

  // Create column definitions with current labels
  const columnDefinitions = useMemo(() => {
    const getLabel = (fieldName: string) => getFieldLabel(fieldName);

    return {
      // Combined controller/circuit column (similar to AlarmsList)
      controller_circuit: createCombinedControllerCircuitColumn(getLabel('controller_circuit')),
      controller_tag: createDeviceLinkColumn(getLabel('controller_tag'), getLabel('controller_tag'), true),
      tag: createDeviceLinkColumn(getLabel('tag'), getLabel('tag'), false),
      status_temperatureSensors_1_temperature: createFormattedColumn(
        'status_temperatureSensors_1_temperature',
        getLabel('status_temperatureSensors_1_temperature'),
        formatTemperature,
        temperatureUnit
      ),
      status_temperatureSensors_2_temperature: createFormattedColumn(
        'status_temperatureSensors_2_temperature',
        getLabel('status_temperatureSensors_2_temperature'),
        formatTemperature,
        temperatureUnit
      ),
      status_loadCurrent: createFormattedColumn(
        'status_loadCurrent',
        getLabel('status_loadCurrent'),
        formatLoadCurrent
      ),
      current_alarms: createDefaultColumn('current_alarms', getLabel('current_alarms'), 300)
    };
  }, [getFieldLabel, temperatureUnit]);

  // Dynamically build columns based on the data returned
  const columns = useReportColumns(
    data,
    columnDefinitions,
    getFieldLabel,
    columnDefinitions.controller_circuit
  );

  const handlePdfGenerationSubmit = () => {
    if (!pdfLoading) {
      dispatch(
        generateTestingReportPdf(
          'multi',
          filters.selectedFilters,
          null,
          null,
          null,
          whitelabel ? 'yes' : 'no'
        )
      );
    }
  };

  const CustomToolbar = () => {
    const apiRef = useGridApiContext();

    const handleExport = (options: any) => {
      // Use apiRef from options if provided (from ReportToolbar), otherwise use context
      const gridApiRef = options.apiRef || apiRef;
      if (gridApiRef && gridApiRef.current) {
        const exportFields = getExportFields(data, columns);
        const dataArray = data?.toJS() || [];

        if (dataArray.length === 0) {
          return;
        }

        // Deduplicate exportFields to ensure no duplicate fields
        // Use a Map to preserve order while ensuring uniqueness
        const uniqueFieldsMap = new Map<string, string>();
        exportFields.forEach(field => {
          if (field && !uniqueFieldsMap.has(field)) {
            uniqueFieldsMap.set(field, field);
          }
        });
        const uniqueExportFields = Array.from(uniqueFieldsMap.keys());

        // Get column headers with proper labels
        const getColumnHeader = (field: string): string => {
          const column = columns.find(col => col.field === field);
          return column?.headerName || getFieldLabel(field) || field;
        };

        // Build CSV manually to ensure hidden columns are included
        const escapeCSVValue = (value: any): string => {
          if (value === null || value === undefined || value === '') {
            return '';
          }
          const stringValue = String(value);
          // Escape quotes and wrap in quotes if contains comma, quote, or newline
          if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
            return `"${stringValue.replace(/"/g, '""')}"`;
          }
          return stringValue;
        };

        // Build header row using unique fields only
        const headers = uniqueExportFields.map(field => escapeCSVValue(getColumnHeader(field)));
        const csvRows = [headers.join(',')];

        // Build data rows using unique fields only
        dataArray.forEach(row => {
          const rowValues = uniqueExportFields.map(field => {
            // Get the raw value from the row data
            const value = row[field];
            // Apply valueFormatter if column has one
            const column = columns.find(col => col.field === field);
            if (column?.valueFormatter && value !== null && value !== undefined) {
              return escapeCSVValue(column.valueFormatter({ value }));
            }
            return escapeCSVValue(value);
          });
          csvRows.push(rowValues.join(','));
        });

        const csvString = csvRows.join('\n');

        // Add UTF-8 BOM to ensure proper encoding in Excel
        const BOM = '\uFEFF';
        const csvWithBOM = BOM + csvString;

        // Create blob with UTF-8 encoding
        const blob = new Blob([csvWithBOM], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', `multi-circuit-report-${new Date().toISOString().split('T')[0]}.csv`);
        link.click();
        URL.revokeObjectURL(url);
      }
    };

    return (
      <ReportToolbar
        onExport={handleExport}
        onPdfDownload={handlePdfGenerationSubmit}
        pdfLoading={pdfLoading}
        showPdf={true}
        exportLabel={t('multiCircuitReport.downloadCsv')}
        pdfLabel={t('multiCircuitReport.downloadPdf')}
      />
    );
  };

  const getRowId = (row: any): string => {
    // Use controller_tag and tag/circuit_tag if available, otherwise fall back to a unique identifier
    if (row.controller_tag && (row.tag || row.circuit_tag)) {
      return `${row.controller_tag || ''}-${row.tag || row.circuit_tag || ''}`;
    }
    // Fallback: use tag if available, otherwise generate a unique ID
    return row.tag || row.circuit_tag || `${row.controller_tag || 'row'}-${Math.random().toString(36).substr(2, 9)}`;
  };

  return (
    <BaseReport
      title={t('multiCircuitReport.title')}
      data={data?.toJS() || []}
      loading={loading}
      columns={columns}
      customToolbar={CustomToolbar}
      getRowId={getRowId}
      noDataMessage={t('reports.noResults')}
      hasRun={hasRun}
    />
  );
}

MultiCircuitReport.propTypes = {
  filters: PropTypes.object
};

MultiCircuitReport.defaultProps = {};
